import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject } from 'rxjs';
import { StaticDialogCloseResult } from './ic-overlay.config';

export class StaticDialogRef<TResult = StaticDialogCloseResult> {
    private readonly closedSubject = new Subject<TResult>();
    private isClosed = false;
    private disposeAnimationFrameId: number | null = null;

    constructor(
        private readonly overlayRef: OverlayRef,
        private readonly inputUpdater?: (name: string, value: unknown) => void,
        private readonly deferDisposeToNextPaint = false,
    ) { }

    close(result: TResult): void {
        if (this.isClosed) {
            return;
        }

        this.isClosed = true;
        this.closedSubject.next(result);
        this.closedSubject.complete();

        if (!this.deferDisposeToNextPaint) {
            this.overlayRef.dispose();
            return;
        }

        this.scheduleDisposeOnNextPaint();
    }

    private scheduleDisposeOnNextPaint(): void {
        if (typeof globalThis.requestAnimationFrame !== 'function') {
            queueMicrotask(() => {
                this.overlayRef.dispose();
            });
            return;
        }

        this.disposeAnimationFrameId = globalThis.requestAnimationFrame(() => {
            this.disposeAnimationFrameId = globalThis.requestAnimationFrame(() => {
                this.disposeAnimationFrameId = null;
                this.overlayRef.dispose();
            });
        });
    }

    disposeImmediately(): void {
        if (this.disposeAnimationFrameId !== null && typeof globalThis.cancelAnimationFrame === 'function') {
            globalThis.cancelAnimationFrame(this.disposeAnimationFrameId);
            this.disposeAnimationFrameId = null;
        }

        if (this.overlayRef.hasAttached()) {
            this.overlayRef.dispose();
        }
    }

    afterClosed(): Observable<TResult> {
        return this.closedSubject.asObservable();
    }

    /** Re-runs the position strategy against the origin's *current* bounding rect — for callers that
     * track their connected overlay's position themselves (e.g. on a scroll or resize the built-in
     * scroll strategy wouldn't otherwise catch) instead of relying solely on `scrollStrategy`. */
    updatePosition(): void {
        if (this.isClosed) {
            return;
        }

        this.overlayRef.updatePosition();
    }

    setInput(name: string, value: unknown): void {
        if (this.isClosed) {
            return;
        }

        this.inputUpdater?.(name, value);
    }
}